import { CountryCode } from 'libphonenumber-js';
import BaseApi, { FetchMethodType } from 'api/BaseApi';
import {  } from 'api/types';
import { ISystemVariableValue, IBasicUser, VariableDataType, ITableViewData } from 'api/types';
import { IdentificationType } from 'components/IdentificationNumberInput/types';
import { ComplexVisualDataType, PrimitiveVisualDataType, VisualDataType } from 'enums/VisualDataType';

export enum VariableAccessPermissionType {
  ReadWrite = 'ReadWrite',
  ReadOnly = 'ReadOnly',
  PartialRead = 'PartialRead',
  NoAccess = 'NoAccess',
}

export type CapVisualAttributes = {
  maxAllowedValue?: string;
  minAllowedValue?: string;
}

export interface ISelectVisualDataTypeAttributes {
  optionsList: string[];
}

export interface IDateVisualAttributes extends CapVisualAttributes {
  dateFormat: string;
}

export interface IIdentificationTypeVisualAttributes {
  identificationNumberType: IdentificationType;
  identificationNumberDescription?: string;
}

export interface IPhoneNumberVisualAttributes {
  phoneNumberFormat: CountryCode;
}

export interface IMonetaryVisualAttributes extends CapVisualAttributes {
  currency: string;
}

export interface ITableColumn {
  id: string;
  name: string;
  systemName: string;
  dataType: VariableDataType;
  visualDataType: VisualDataType;
  visualAttributes: VariableVisualAttributes;
  required?: boolean;
}

export interface ITableVisualAttributes {
  columns: ITableColumn[];
}

export type VariableVisualAttributes<Type extends VisualDataType = VisualDataType> =
  Type extends PrimitiveVisualDataType.Date ? IDateVisualAttributes :
  Type extends PrimitiveVisualDataType.DateTime ? IDateVisualAttributes :
  Type extends PrimitiveVisualDataType.IdentificationNumber ? IIdentificationTypeVisualAttributes :
  Type extends PrimitiveVisualDataType.PhoneNumber ? IPhoneNumberVisualAttributes :
  Type extends PrimitiveVisualDataType.Select ? ISelectVisualDataTypeAttributes :
  Type extends PrimitiveVisualDataType.MultiSelect ? ISelectVisualDataTypeAttributes :
  Type extends PrimitiveVisualDataType.Monetary ? IMonetaryVisualAttributes :
  Type extends ComplexVisualDataType.Table ? ITableVisualAttributes :
  Type extends PrimitiveVisualDataType.Number ? CapVisualAttributes :
  Type extends PrimitiveVisualDataType.Percentage ? CapVisualAttributes : never;


export interface IBasicVariable {
  id: string;
  name: string;
  systemName: string;
  organizationId: string;
  organizationVersion: number | null;
  isArchived: boolean;
  dataType: VariableDataType;
  visualDataType: VisualDataType;
}

export interface IVariable extends IBasicVariable {
  visualAttributes: VariableVisualAttributes;
  isStandard: boolean;
  dependsOn?: string | null;
  description?: string;
  createdBy?: IBasicUser | null;
  updatedBy?: IBasicUser | null;
  updatedAt: Date;
  createdAt: Date;
}


export type VariableVisualFields = {
  visualDataType: VisualDataType;
  visualAttributes: VariableVisualAttributes;
}

export interface ISystemVariable {
  id: string;
  name: string;
  format?: (value: ISystemVariableValue) => string;
}

export interface IVariablesDataFilters {
  systemNames?: string[];
  showArchived?: boolean;
}

export interface IVariablesApi {
  search(filters: IVariablesDataFilters, abortSignal?: AbortSignal): Promise<ITableViewData<IVariable>>;
}

export default class VariablesRestApi extends BaseApi implements IVariablesApi {
  protected resourceName = 'variables';

  public async search(filters: IVariablesDataFilters) {
    const params: Record<string, unknown> = {};

    if (filters) {
      const {
        systemNames,
      } = filters;

      params.systemNames = systemNames;
    }

    return this.fetch<ITableViewData<IVariable>>(`/${this.resourceName}/search`, {
      method: FetchMethodType.PUT,
      body: params,
    });
  }
}
